import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@app/core/services/auth.service';
import { CoreService } from '@app/core/services/core.service';
import { HealthProfessionalService } from '@app/modules/health-professional/services/health-professional.service';
import { ModalAddPatientComponent } from '@app/modules/patients/pages/modal-add-patient/modal-add-patient.component';
import { ModalSearchComponent } from '@app/modules/patients/pages/modal-search/modal-search.component';
import { PatientDetailsComponent } from '@app/modules/patients/pages/patient-details/patient-details.component';
import { nativeAsync } from '@app/shared/decorators/nativeAsync';
import { HealthProfessional, Patient } from '@app/shared/models';
import { User } from '@app/shared/models/decodedLoginToken';
import moment from 'moment';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ModalSelectPetComponent } from '../modal-select-pet/modal-select-pet.component';
import { ModalSwitchResponsibleComponent } from '../modal-switch-responsible/modal-switch-responsible.component';
import { PetPatient } from '@app/shared/models/pet-patient';
import { DataBigboostConsult } from '@app/shared/models/paciente';
import { PatientsService } from '../../services/patients.service';
import { PetPatientService } from '../../services/pet-patient.service';
import { AppToastService } from '@app/shared/services/app-toast.service';

const NUMBER_MONTH_OCTOBER = 10;

@Component({
  selector: 'app-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class IndexComponent implements OnInit, AfterViewInit {
  userId: string;
  allChecked = false;
  disabledButton = true;
  checkedNumber = 0;
  displayData: Array<{ name: string; age: number; address: string; checked: boolean }> = [];
  operating = false;
  dataSet: Patient[] = [];
  patient: Patient[];
  prescriber: HealthProfessional;
  loading: boolean = false;
  loadingSpinner: boolean = false;
  searchForm: FormGroup;
  indeterminate = false;
  totalRecord = 0;
  pageSize = 10;
  skip = 0;
  query: string = null;

  @ViewChild(PatientDetailsComponent, { static: true })
  modalPatient: PatientDetailsComponent;

  @ViewChild('inputSearch', { static: true }) inputSearch: ElementRef<HTMLInputElement>;

  scrollConfig = { y: '510px' };
  pageIndex = 1;

  filtersFast = {
    womenOver40: false,
    minors: false,
    adults: false
  };

  isMobile = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isMobile = this.coreService.isViewPortMobile();
  }

  constructor(
    private router: Router,
    private healthProfessionalService: HealthProfessionalService,
    private authService: AuthService,
    private fb: FormBuilder,
    private modalService: BsModalService,
    private nzModalService: NzModalService,
    public coreService: CoreService,
    private cdr: ChangeDetectorRef,
    private patientService: PatientsService,
    private petPatientService: PetPatientService,
    private notification: AppToastService
  ) {
    this.onResize();
  }

  @nativeAsync
  async ngOnInit() {
    this.initForm();
    this.userId = this.user._id;
    await this.getHealthProfessionalData(this.user._id);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.inputSearch.nativeElement.focus();
    }, 0);
    this.cdr.detectChanges();
  }

  get pinkOctoberCampaignEnabled() {
    return moment().month() + 1 === NUMBER_MONTH_OCTOBER;
  }

  get user(): User {
    return this.authService.user();
  }

  get isVeterinarian(): boolean {
    return this.prescriber && this.prescriber.professionalType === 'veterinarian';
  }

  @nativeAsync
  async getHealthProfessionalData(userId: string) {
    this.loading = true;
    this.prescriber = await this.healthProfessionalService.getHealthProfessional(userId).toPromise();

    this.getPatients(this.query);
  }

  requestPatients() {
    this.loading = true;
    this.healthProfessionalService
      .getPatientsByUserIdWithPagination(
        this.query,
        this.user._id,
        this.pageSize,
        this.skip,
        this.filtersFast
      )
      .subscribe(
        data => {
          this.dataSet = data.patients;
          this.totalRecord = data.totalRecord;
          this.loading = false;
        },
        err => {
          console.error(err);
          this.loading = false;
        }
      );
  }

  requestPetPatients() {
    this.loading = true;
    this.healthProfessionalService.getPetsWithPagination(this.query, this.pageSize, this.skip).subscribe(
      data => {
        this.dataSet = data.pets;
        this.totalRecord = data.totalRecord;
        this.loading = false;
        setTimeout(() => {
          this.inputSearch.nativeElement.focus();
        }, 0);
      },
      err => {
        console.error(err);
        this.loading = false;
      }
    );
  }

  @nativeAsync
  async showModalRemovePatient(patientId: string) {
    this.nzModalService.confirm({
      nzTitle: 'Alerta',
      nzContent: 'Tem certeza que deseja remover este paciente da sua lista',
      nzOnOk: async () => {
        if (this.isVeterinarian) {
          await this.healthProfessionalService
            .removePetPatient(this.prescriber._id, patientId)
            .toPromise();
        } else {
          await this.healthProfessionalService.removePatient(this.prescriber._id, patientId).toPromise();
        }
        this.getPatients(this.query);
      }
    });
  }

  getPatients(query: string) {
    this.query = query;
    this.skip = 1;
    this.pageIndex = 1;

    this.isVeterinarian ? this.requestPetPatients() : this.requestPatients();
  }

  getAllPatientsNames(allNames) {
    let result = '';
    for (let i = 0; i < allNames.length; i++) {
      result = this.dataSet[i].name;
    }
    return result;
  }

  private initForm() {
    this.searchForm = this.fb.group({
      query: this.fb.control('')
    });

    this.searchForm
      .get('query')
      .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => this.getPatients(value));
  }

  clearQuery() {
    this.searchForm.get('query').setValue('');
  }

  currentPageDataChange(
    $event: Array<{ name: string; age: number; address: string; checked: boolean }>
  ): void {
    this.displayData = $event;
  }

  refreshStatus(): void {
    const allChecked = this.displayData.every(value => value.checked);
    const allUnChecked = this.displayData.every(value => !value.checked);
    this.allChecked = allChecked;
    this.indeterminate = !allChecked && !allUnChecked;
  }

  checkAll(value: boolean): void {
    this.displayData.forEach(data => (data.checked = value));
    this.refreshStatus();
  }

  togglePatientModalDetails(event: Event, patient: any): void {
    event.preventDefault();
    this.modalPatient.createTplModal(patient, this.prescriber);
    this.modalPatient.update.subscribe(saved =>
      this.isVeterinarian ? this.requestPetPatients() : this.requestPatients()
    );
  }

  redirectToPatientPrescriptions(patientId: string) {
    this.router.navigate(['historic'], { queryParams: { patientId } });
  }

  redirectToNewPrescription(patient) {
    localStorage.setItem('patient', JSON.stringify(patient));
    const url =
      this.isVeterinarian || this.authService.hasAccessNewModulePrescription
        ? '/document/new'
        : '/prescription/new';
    this.router.navigate([url]);
  }

  showModalSearch(cpf?: string) {
    const initialState = {
      user: this.user,
      prescriber: this.prescriber,
      addPatient: true,
      cpf
    };
    const modal = this.modalService.show(ModalSearchComponent, {
      initialState,
      backdrop: 'static',
      keyboard: false
    });
    modal.content.updateListPatientEvent.subscribe(patient => {
      this.query = null;
      this.skip = 1;
      this.requestPatients();
    });
    modal.content.openModalAddPatientEvent.subscribe(consult => this.showModalAddPatient(consult));
    modal.content.openModalSelectPetEvent.subscribe(consult => this.showModalSelectPet(consult));
    modal.content.loadingSpinnerEvent.subscribe((value: boolean) => (this.loadingSpinner = value));

    if (cpf) {
      setInterval(() => modal.hide(), 1);
    }
  }

  private showModalAddPatient(consult, sucess?: (patient: Patient) => void) {
    const initialState = {
      prescriber: this.prescriber,
      patient: consult.patient,
      isResponsible: consult.isResponsible,
      existingPatient: consult.existingPatient
    };
    const modal = this.modalService.show(ModalAddPatientComponent, {
      class: 'modal-lg',
      initialState,
      backdrop: 'static',
      keyboard: false
    });
    modal.content.updateListPatientEvent.subscribe((patientSaved: Patient) => {
      if (!sucess) {
        this.query = null;
        this.skip = 1;
        this.requestPatients();
      } else {
        sucess(patientSaved);
      }
    });
  }

  private showModalSelectPet(consult) {
    const initialState = {
      prescriber: this.prescriber,
      pets: consult.patient.pets,
      responsible: consult.patient
    };
    const modal = this.modalService.show(ModalSelectPetComponent, {
      initialState,
      backdrop: 'static',
      keyboard: false
    });

    modal.content.submit.subscribe(_ => {
      this.query = null;
      this.skip = 1;
      this.requestPetPatients();
      this.modalService.hide(1);
    });
  }

  pageIndexChanged(pageIndex: number): void {
    this.loading = true;
    this.skip = pageIndex;
    this.pageIndex = pageIndex;
    this.isVeterinarian ? this.requestPetPatients() : this.requestPatients();
  }

  toggleFiltersFast(keyFilter: string) {
    Object.entries(this.filtersFast).forEach(([key, value]) => {
      if (key === keyFilter) {
        this.filtersFast[keyFilter] = !value;
      } else {
        this.filtersFast[key] = false;
      }
    });
    this.requestPatients();
  }

  showModalSwitchResponsible(patient: Patient | PetPatient) {
    const initialState = {
      patient,
      isVeterinarian: this.isVeterinarian
    };
    const modal = this.modalService.show(ModalSwitchResponsibleComponent, {
      initialState,
      backdrop: 'static',
      keyboard: true,
      class: 'modal-sm'
    });

    const updateResponsilbePatient = (newReponsible: Patient) => {
      this.dataSet = this.dataSet.map(item => {
        if (item._id === patient._id) {
          return {
            ...item,
            responsible: newReponsible
          };
        }
        return item;
      });
    };

    modal.content.sucess.subscribe(updateResponsilbePatient);

    modal.content.newPatientRegister.subscribe((consult: DataBigboostConsult) => {
      const initialState = {
        patient: consult,
        isResponsible: false,
        existingPatient: false
      };
      this.showModalAddPatient(initialState, (newPatient: Patient) => {
        const execute = this.isVeterinarian
          ? this.petPatientService.switchReponsible(patient._id, newPatient.cpf)
          : this.patientService.switchReponsible(patient._id, newPatient.cpf);

        execute.subscribe(
          () => {
            updateResponsilbePatient(newPatient);
            this.dataSet.push(newPatient);
          },
          () => {
            this.notification.notify(
              'error',
              'Ocorreu um erro ao alterar o responsável do paciente. Favor entrar em contato com administradores!'
            );
          }
        );
      });
    });
  }
}
